import React, { useEffect, useRef, useState } from 'react';

import FixedLoading from 'components/common/FixedLoading';
import { useTranslation } from 'react-i18next';

import { useGoogleSignIn } from '../hooks/useGoogleSignIn';
import { GOOGLE_CLIENT_ID, isInAppBrowser, loadGoogleIdentity, setGoogleCredentialHandler } from '../lib';

type GoogleSignInButtonProps = {
  // 버튼 아래 "또는 이메일로" 구분선을 함께 그릴지
  hasDivider?: boolean;
};

// 구글 공식 버튼의 최대 폭(px) — 모달 폭에 맞춰 그 이하로 그린다
const GOOGLE_BUTTON_MAX_WIDTH = 400;

/** 구글 공식 로그인 버튼. 클라이언트 ID가 없거나 앱 안 브라우저(인스타그램 등)에선 아무것도 그리지 않는다 */
const GoogleSignInButton = ({ hasDivider = true }: GoogleSignInButtonProps) => {
  const { t, i18n } = useTranslation('auth');
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const { signInWithGoogle, isPending } = useGoogleSignIn();
  const isAvailable = GOOGLE_CLIENT_ID !== '' && !isInAppBrowser(navigator.userAgent);

  // 콜백이 최신 핸들러를 쓰도록 ref 로 전달
  const signInRef = useRef(signInWithGoogle);
  signInRef.current = signInWithGoogle;

  useEffect(() => {
    if (!isAvailable) return;
    let isCancelled = false;

    loadGoogleIdentity()
      .then((googleId) => {
        if (isCancelled || !buttonRef.current) return;
        setGoogleCredentialHandler(googleId, (idToken) => signInRef.current(idToken));
        googleId.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: Math.min(buttonRef.current.offsetWidth, GOOGLE_BUTTON_MAX_WIDTH),
          locale: i18n.language,
        });
        setIsReady(true);
      })
      .catch(() => undefined); // 스크립트를 못 받으면 버튼 없이 이메일 로그인만 노출

    return () => {
      isCancelled = true;
    };
  }, [isAvailable, i18n.language]);

  if (!isAvailable) return null;

  return (
    <div className="flex w-full flex-col gap-y-3">
      <div ref={buttonRef} className="flex h-10 w-full justify-center" />
      {hasDivider && isReady && (
        <div className="flex w-full items-center gap-x-3 text-c4 text-gray-40">
          <span className="h-px flex-1 bg-gray-20" />
          {t('google.or')}
          <span className="h-px flex-1 bg-gray-20" />
        </div>
      )}
      {isPending && <FixedLoading />}
    </div>
  );
};

export default GoogleSignInButton;
